import { Button } from '@acpaas-ui/react-components';
import _, { has } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchReset } from '../../actions/fetch.actions';
import AdminContactCityFields from '../../components/admin/contacts/AdminContactCityFields';
import AdminAddContactOrganizationsFields from '../../components/admin/contacts/AdminContactOrganizationsFields';
import AdminContactSharedFields from '../../components/admin/contacts/AdminContactSharedFields';
import { INPUT_STATES } from '../../components/forms/forms.utils';
import TextArea from '../../components/forms/TextArea';
import { MODALS } from '../../constants/modals.constants';
import formValidators from '../../form-validators';
import { useModal } from '../../hooks/useModal.hook';
import { getCanSubmitContact } from '../../lib/contact.utils';
import { ADMIN_CONTACT_PARTNER_ORGANIZATIONS_RESOURCE } from '../../resources/admin/admin.contact.partner.organizations.resource';
import { ADMIN_CONTACT_SCHOOL_ORGANIZATIONS_RESOURCE } from '../../resources/admin/admin.contact.school.organizations.resource';
import { ADMIN_CONTACTS_RESOURCE } from '../../resources/admin/admin.contacts.resource';
import { currentUserSelector } from '../../selectors/auth.selectors';
import contactsService from '../../services/api/contacts.service';
import { ERROR_TEXTS, GENERIC_ERROR } from '../../services/response.validators';
import Modal from '../Modal';
import ModalTitle from '../ModalTitle';
import ContactEmailExistsWarningModal from './ContactEmailExistsWarningModal';
import ContactRoleEditErrorModal from './ContactRoleEditErrorModal';
import ContactUpdateNoPermissionModal from './ContactUpdateNoPermissionModal';
import contactsPermissionsService from '../../services/permissions/contacts.permissions.service';

const INPUT_NAME = 'spo-update-contact-textfield';

const UpdateContactModal = ({ isOpen, onClose, onDelete, form, fetchNotApprovedCount }) => {
  const dispatch = useDispatch();
  const modalContentRef = useRef(null);
  const [validation, setValidation] = useState({});
  const [updates, setUpdates] = useState([]);
  const [originalEmail] = useState(form?.data?.email);
  const { data, load, input, isSubmitting, submitStart, submitOk, submitError, reset, error } = form;
  const contactEmailExistsWarningModal = useModal(MODALS.CONTACT_EMAIL_EXISTS_WARNING_MODAL);
  const contactUpdateNoPermissionModal = useModal(MODALS.CONTACT_UPDATE_NO_PERMISSION_MODAL);
  const contactRoleEditErrorModal = useModal(MODALS.CONTACT_ROLE_EDIT_ERROR_MODAL);
  const [initRoles, setInitRoles] = useState(data?.roles);

  const user = useSelector(currentUserSelector);
  const canUpdate = contactsPermissionsService.canUpdateContact(user, data);

  const validators = {
    email: formValidators.email,
    phone: formValidators.phoneSimple
  };

  const handleConfirm = () => {
    submitStart();
    let updatedFields = [...updates, 'type'];
    if (data?.email === originalEmail) {
      updatedFields = updatedFields?.filter((f) => f !== 'email');
    }
    const updateBody = { ..._.pick(data, updatedFields) };

    const addedRoles = data.roles.filter((el) => !has(el, 'id'));
    const updatedRoles = data.roles.filter(
      (el) => has(el, 'id') && initRoles.find((item) => item?.id === el.id && item !== el)
    );
    const deletedRoles = initRoles.filter(
      (el) => !data.roles.find((item) => item?.id === el?.id) && !updatedRoles.find((item) => item?.id === el?.id)
    );

    contactsService.updateContactWithRoleUpdates(
      data.id,
      updateBody,
      { addedRoles, updatedRoles, deletedRoles },
      {
        onSuccess: () => {
          submitOk();
          fetchNotApprovedCount(false);
          reset();
          handleClose();
          dispatch(fetchReset(ADMIN_CONTACTS_RESOURCE));
        },
        onError: (error) => {
          submitError();
          if (error === ERROR_TEXTS.NO_PERMISSION) {
            contactUpdateNoPermissionModal.onOpen();
          }
        }
      }
    );
  };

  const handleClose = () => {
    load({});
    dispatch(fetchReset(ADMIN_CONTACT_PARTNER_ORGANIZATIONS_RESOURCE));
    dispatch(fetchReset(ADMIN_CONTACT_SCHOOL_ORGANIZATIONS_RESOURCE));
    onClose();
  };

  const handleEnter = (event) => {
    if (event.code === 'Enter' && getCanSubmitContact(data, validation)) {
      handleConfirm();
    }
  };

  const handleRoleAdd = (roleData) => {
    handleInput([...data?.roles, { ...roleData, index: (data?.roles || []).length }], 'roles');
  };

  const handleRoleUpdate = (roleData) => {
    handleInput(
      data?.roles?.map((r, index) => {
        if (index === roleData?.index) {
          return roleData;
        }

        return r;
      }),
      'roles'
    );
  };

  const handleRoleDelete = (index) => {
    handleInput(
      data?.roles?.filter((r, i) => i !== index),
      'roles'
    );
  };

  const handleInput = (value, field) => {
    setUpdates(_.uniq([...updates, field]));
    if (validators[field]) {
      setValidation({ ...validation, [field]: validators[field](value) });
    }
    input(value, field);
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        document.getElementsByName(INPUT_NAME)?.[0]?.focus();
      }, 10);
    }
  }, [isOpen]);

  const sharedSubfieldsProps = {
    isEdit: true,
    data,
    validation,
    isSubmitting,
    canUpdate,
    onInput: handleInput,
    parentElement: modalContentRef.current
  };

  return (
    <Modal
      ref={modalContentRef}
      className="spo-o-add-theme-modal"
      title={<ModalTitle hasError={!!error} title="Contact aanpassen" />}
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      canConfirm={getCanSubmitContact(data, validation, true)}
      disableConfirm={!canUpdate}
      isSubmitting={isSubmitting}
      onConfirmLabel={'Aanpassen'}
      onConfirmIcon="ai-check-1"
      extraFooterActions={
        contactsPermissionsService.canDeleteContact(user, data) ? (
          <Button type="danger" outline disabled={isSubmitting} onClick={onDelete}>
            Verwijderen
          </Button>
        ) : null
      }
      width={1200}
    >
      <AdminContactSharedFields inputName={INPUT_NAME} onEnter={handleEnter} {...sharedSubfieldsProps} />
      <AdminAddContactOrganizationsFields
        type={data?.type}
        roles={data?.roles}
        onAdd={handleRoleAdd}
        onUpdate={handleRoleUpdate}
        onDelete={handleRoleDelete}
        {...sharedSubfieldsProps}
      />
      <AdminContactCityFields inputName={INPUT_NAME} onEnter={handleEnter} {...sharedSubfieldsProps} />
      <TextArea
        label="Opmerkingen"
        name={INPUT_NAME}
        value={data?.remark}
        state={INPUT_STATES.DEFAULT}
        onKeyDown={handleEnter}
        className="u-margin-top"
        onChange={(value) => handleInput(value, 'remark')}
        disabled={isSubmitting}
        readOnly={!canUpdate}
      />
      {!!error && <p className="u-text-danger">{error}</p>}
      <ContactEmailExistsWarningModal {...contactEmailExistsWarningModal} />
      <ContactUpdateNoPermissionModal {...contactUpdateNoPermissionModal} />
      <ContactRoleEditErrorModal {...contactRoleEditErrorModal} />
    </Modal>
  );
};

export default UpdateContactModal;
